import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, query, where, documentId, getDocs } from 'firebase/firestore';
import { BigQueryService } from '../../services/bigQueryService';
import { ArrowPathIcon, XCircleIcon, ChartBarIcon, ClockIcon, CubeIcon, BuildingOfficeIcon, BoltIcon } from '../ui/Icons';
import useEsMovil from '../../hooks/useEsMovil';
import useConteoAnimado from '../../hooks/useConteoAnimado';
import { ESTILO_TILE, Sparkline, BadgeTendencia, DonaEstados } from './widgetsUI';

// Estilos responsivos para móvil
const estilosResponsivos = `
  @media (max-width: 768px) {
    .estadisticas-container {
      margin-top: 20px !important;
    }

    .estadisticas-header {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center;
      margin-bottom: 15px !important;
    }

    .estadisticas-header-der {
      width: 100%;
      flex-direction: column !important;
      gap: 8px !important;
    }

    .estadisticas-header h2 {
      font-size: 18px !important;
      margin: 0 !important;
    }

    .estadisticas-header small {
      font-size: 11px !important;
    }

    .btn-actualizar {
      width: 100% !important;
      padding: 10px 16px !important;
      font-size: 13px !important;
    }

    .kpi-grid-dia {
      grid-template-columns: 1fr 1fr !important;
      gap: 8px !important;
    }

    .kpi-tile-dia {
      padding: 10px !important;
    }

    .kpi-valor-dia {
      font-size: 19px !important;
    }

    .viz-grid-dia {
      grid-template-columns: 1fr !important;
      gap: 10px !important;
    }

    .viz-card-dia {
      padding: 12px !important;
    }

    .loading-container,
    .error-container {
      padding: 15px !important;
      margin-top: 15px !important;
      font-size: 13px !important;
    }
  }
`;

// ── Helpers propios del widget ──────────────────────────────────────────────
// (Las piezas visuales compartidas — ESTILO_TILE, Sparkline, BadgeTendencia,
// DonaEstados — viven en ./widgetsUI.js, comunes a todos los widgets.)

const DIAS_CORTOS = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'];
// Para el texto del badge de tendencia ("vs viernes" cuando hoy es lunes).
const DIAS_LARGOS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

const fmtFechaISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function EstadisticasDiaWidget() {
  const [estadisticas, setEstadisticas] = useState(null);
  const [serie, setSerie] = useState([]); // últimos 7 días hábiles (incluye hoy si es hábil)
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    cargarEstadisticas(false);
  }, []);

  const cargarEstadisticas = async (forzar = false) => {
    try {
      setCargando(true);
      setError('');

      const hoy = new Date();
      const fechaHoy = fmtFechaISO(hoy);

      // Los últimos 7 días HÁBILES (lunes a viernes), del más viejo al más nuevo.
      // Sábado y domingo no se despacha nada: sus barras salían siempre en cero y
      // solo hacían ruido — ahora en su lugar se ven los días de trabajo anteriores.
      // Si hoy cae sábado o domingo, la última barra es el viernes.
      const fechasHabiles = [];
      const cursor = new Date(hoy);
      while (fechasHabiles.length < 7) {
        const dia = cursor.getDay();
        if (dia !== 0 && dia !== 6) {
          fechasHabiles.unshift({ id: fmtFechaISO(cursor), diaSemana: dia });
        }
        cursor.setDate(cursor.getDate() - 1);
      }

      // UNA query con los IDs exactos (los IDs de `estadisticas_diarias` son fechas
      // YYYY-MM-DD): ≤8 lecturas por apertura, sin Cloud Function y sin cold start.
      // Se piden por `in` y no por rango justamente para NO traer los docs del fin de
      // semana, que ya no se usan. `fechaHoy` va siempre en la lista, aunque hoy caiga
      // sábado o domingo, porque de ese doc salen los KPIs de arriba y el cache que
      // evita la llamada a la Cloud Function. De acá salen el HOY, la comparación
      // contra el día hábil anterior y las sparklines.
      const ids = fechasHabiles.map((f) => f.id);
      if (!ids.includes(fechaHoy)) ids.push(fechaHoy);
      const snap = await getDocs(query(
        collection(db, 'estadisticas_diarias'),
        where(documentId(), 'in', ids)
      ));
      const porFecha = {};
      snap.forEach((d) => { porFecha[d.id] = d.data(); });

      const dias = fechasHabiles.map((f) => ({
        fecha: f.id,
        diaSemana: f.diaSemana,
        etiqueta: f.id === fechaHoy ? 'Hoy' : DIAS_CORTOS[f.diaSemana],
        esHoy: f.id === fechaHoy,
        data: porFecha[f.id] || null
      }));
      setSerie(dias);

      // El botón Actualizar (forzar) llama a la CF, que recalcula con
      // BigQuery. Si el doc de hoy aún no existe, también cae a la CF.
      if (!forzar && porFecha[fechaHoy]) {
        setEstadisticas({ ...porFecha[fechaHoy], fromCache: true });
        setFromCache(true);
        return;
      }

      const stats = await BigQueryService.obtenerEstadisticasPedidosDia(fechaHoy, forzar);
      setEstadisticas(stats);
      setFromCache(stats.fromCache);
      // Reflejar el recálculo de hoy en la serie (barras y sparklines)
      setSerie((prev) => prev.map((p) => (p.esHoy ? { ...p, data: stats } : p)));
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      setError('Error al cargar estadísticas');
    } finally {
      setCargando(false);
    }
  };

  const formatearMoneda = (monto) => {
    return 'Q ' + parseFloat(monto).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatearFechaHora = (timestamp) => {
    if (!timestamp) return 'No disponible';
    
    try {
      let fecha;
      
      if (timestamp._seconds) {
        fecha = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds) {
        fecha = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        fecha = timestamp.toDate();
      } else {
        fecha = new Date(timestamp);
      }
      
      if (isNaN(fecha.getTime())) {
        console.error('Fecha inválida:', timestamp);
        return 'No disponible';
      }
      
      const opciones = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Guatemala'
      };
      
      const hora = fecha.toLocaleTimeString('es-GT', opciones);
      
      return hora;
    } catch (error) {
      console.error('Error formateando fecha:', error, timestamp);
      return 'No disponible';
    }
  };

  const isMobile = useEsMovil();

  // Números con conteo animado (los hooks van antes de cualquier return)
  const ticketHoy = (estadisticas?.pedidosValidos || 0) > 0
    ? (estadisticas.totalVentas || 0) / estadisticas.pedidosValidos
    : 0;
  const pedidosAnimado = useConteoAnimado(estadisticas?.pedidosValidos || 0);
  const ventasAnimado = useConteoAnimado(estadisticas?.totalVentas || 0);
  const ventasSinEnvioAnimado = useConteoAnimado(estadisticas?.totalVentasSinEnvio || 0);
  const ticketAnimado = useConteoAnimado(ticketHoy);

  // Barras y dona — se llenan al aparecer los datos
  const [barrasListas, setBarrasListas] = useState(false);
  useEffect(() => {
    if (!estadisticas) return;
    setBarrasListas(false);
    const t = setTimeout(() => setBarrasListas(true), 60);
    return () => clearTimeout(t);
  }, [estadisticas]);

  const montoCapital = estadisticas?.desgloseCapitalForza?.Capital?.monto || 0;
  const montoForza = estadisticas?.desgloseCapitalForza?.FORZA?.monto || 0;
  const totalCapitalForza = montoCapital + montoForza;
  const pctCapital = totalCapitalForza > 0 ? (montoCapital / totalCapitalForza) * 100 : 0;

  // Tendencias contra el TOTAL del día hábil anterior (el penúltimo de la serie).
  // Los lunes eso es el viernes: antes se comparaba contra el domingo, que siempre
  // da 0, y por eso los lunes no se veía ninguna tendencia. La etiqueta del badge
  // dice contra qué día se está comparando, para que el número no engañe.
  const diaAnterior = serie.length >= 2 ? serie[serie.length - 2] : null;
  const ayerData = diaAnterior?.data || null;
  const ayerNaturalISO = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return fmtFechaISO(d);
  })();
  const compEsAyer = !diaAnterior || diaAnterior.fecha === ayerNaturalISO;
  const compNombre = diaAnterior ? DIAS_LARGOS[diaAnterior.diaSemana] : '';
  const compEtiqueta = compEsAyer ? 'vs ayer' : `vs ${compNombre}`;
  const compTextoIgual = compEsAyer ? 'igual que ayer' : `igual que el ${compNombre}`;
  const compTitulo = compEsAyer
    ? 'Comparado contra el total de ayer'
    : `Comparado contra el total del ${compNombre} (el día hábil anterior)`;
  const tendencia = (hoyV, ayerV) => (ayerV > 0 ? ((hoyV - ayerV) / ayerV) * 100 : null);
  const tPedidos = tendencia(estadisticas?.pedidosValidos || 0, ayerData?.pedidosValidos || 0);
  const tVentas = tendencia(estadisticas?.totalVentas || 0, ayerData?.totalVentas || 0);
  const tSinEnvio = tendencia(estadisticas?.totalVentasSinEnvio || 0, ayerData?.totalVentasSinEnvio || 0);
  const ticketAyer = (ayerData?.pedidosValidos || 0) > 0 ? (ayerData.totalVentas || 0) / ayerData.pedidosValidos : 0;
  const tTicket = tendencia(ticketHoy, ticketAyer);

  // Series de 7 días hábiles para las sparklines y las barras
  const spPedidos = serie.map((p) => p.data?.pedidosValidos || 0);
  const spVentas = serie.map((p) => p.data?.totalVentas || 0);
  const spSinEnvio = serie.map((p) => p.data?.totalVentasSinEnvio || 0);
  const spTicket = serie.map((p) => ((p.data?.pedidosValidos || 0) > 0 ? (p.data.totalVentas || 0) / p.data.pedidosValidos : 0));
  const maxSerie = Math.max(...spPedidos, 1);

  if (cargando) {
    // Skeleton con la silueta real del contenido (3 cards)
    return (
      <>
        <style>{estilosResponsivos}</style>
        <div className="estadisticas-container" style={{ marginTop: isMobile ? '20px' : '30px', maxWidth: '1200px', marginLeft: 'auto', marginRight: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: isMobile ? '15px' : '16px' }}>
            <div className="skeleton" style={{ width: '240px', height: '26px', borderRadius: 'var(--radio-sm)' }} />
            <div className="skeleton" style={{ width: '280px', height: '34px', borderRadius: 'var(--radio-sm)' }} />
          </div>
          <div className="kpi-grid-dia" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
            gap: isMobile ? '8px' : '14px',
            marginBottom: isMobile ? '10px' : '14px'
          }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton" style={{ height: '108px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
          <div className="viz-grid-dia" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            gap: isMobile ? '10px' : '14px'
          }}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton" style={{ height: '170px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <style>{estilosResponsivos}</style>
        <div className="error-container" style={{
          padding: isMobile ? '15px' : '20px',
          textAlign: 'center',
          backgroundColor: 'var(--color-peligro-fondo)',
          borderRadius: '8px',
          marginTop: isMobile ? '15px' : '20px',
          border: '1px solid var(--color-peligro)'
        }}>
          <p style={{ fontSize: isMobile ? '13px' : '14px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', color:'var(--color-peligro-texto)' }}>
            <span style={{color:'var(--color-peligro)', display:'inline-flex'}}><XCircleIcon className="w-5 h-5"/></span> {error}
          </p>
          <button
            onClick={() => cargarEstadisticas(true)}
            className="btn-actualizar btn-press hover-bright"
            style={{
              marginTop: '10px',
              height: '38px',
              boxSizing: 'border-box',
              padding: '0 20px',
              backgroundColor: 'var(--color-primario)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              fontSize: '14px'
            }}
          >
            <ArrowPathIcon className="w-4 h-4"/> Reintentar
          </button>
        </div>
      </>
    );
  }

  if (!estadisticas) return null;

  return (
    <>
      <style>{estilosResponsivos}</style>
      <div className="estadisticas-container" style={{ marginTop: isMobile ? '20px' : '30px', maxWidth: '1200px', marginLeft: 'auto', marginRight: 'auto' }}>
        {/* Header estilo dashboard: título a la izquierda, metadata + botón a la
            derecha (en móvil el CSS lo apila y centra) */}
        <div className="estadisticas-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: isMobile ? '15px' : '16px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: isMobile ? '18px' : '22px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{color:'var(--color-primario-fuerte)', display:'inline-flex'}}><ChartBarIcon className="w-6 h-6"/></span> Estadísticas del Día
          </h2>
          <div className="estadisticas-header-der" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {estadisticas.ultimaActualizacion && (
              <small
                title={fromCache ? 'Datos precalculados (se actualizan solos cada hora)' : 'Recalculado al momento'}
                style={{ color: 'var(--color-texto-secundario)', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
              >
                <span style={{ display: 'inline-flex' }}><ClockIcon className="w-3 h-3"/></span>
                Actualizado {formatearFechaHora(estadisticas.ultimaActualizacion)} · cada hora
              </small>
            )}
            <button
              onClick={() => cargarEstadisticas(true)}
              className="btn-actualizar btn-press hover-bright"
              style={{
                height: '38px',
                boxSizing: 'border-box',
                padding: '0 20px',
                backgroundColor: 'var(--color-primario)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              title="Forzar recálculo de estadísticas"
            >
              <ArrowPathIcon className="w-4 h-4"/> Actualizar
            </button>
          </div>
        </div>

        {/* Fila de KPIs — número grande + tendencia vs ayer + sparkline 7 días */}
        <div className="kpi-grid-dia stagger-list" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
          gap: isMobile ? '8px' : '14px',
          marginBottom: isMobile ? '10px' : '14px'
        }}>
          <div className="card kpi-tile-dia" style={ESTILO_TILE} title="Estados válidos: POR VERIFICAR, POR REVISAR IA, CONGELADO, POR ENVIAR, ENVIADO">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Pedidos válidos</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span className="kpi-valor-dia" style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(pedidosAnimado)}
              </span>
              <BadgeTendencia pct={tPedidos} etiqueta={compEtiqueta} textoIgual={compTextoIgual} titulo={compTitulo} />
            </div>
            <Sparkline valores={spPedidos} color="var(--color-primario)" />
          </div>

          <div className="card kpi-tile-dia" style={ESTILO_TILE} title="Ventas de los pedidos válidos de hoy">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Ventas del día</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span className="kpi-valor-dia" style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-exito)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                {formatearMoneda(ventasAnimado)}
              </span>
              <BadgeTendencia pct={tVentas} etiqueta={compEtiqueta} textoIgual={compTextoIgual} titulo={compTitulo} />
            </div>
            <Sparkline valores={spVentas} color="var(--color-exito)" />
          </div>

          <div className="card kpi-tile-dia" style={ESTILO_TILE} title="Ventas de hoy excluyendo gastos de envío">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Sin envío</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span className="kpi-valor-dia" style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-info-texto)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                {formatearMoneda(ventasSinEnvioAnimado)}
              </span>
              <BadgeTendencia pct={tSinEnvio} etiqueta={compEtiqueta} textoIgual={compTextoIgual} titulo={compTitulo} />
            </div>
            <Sparkline valores={spSinEnvio} color="var(--color-info)" />
          </div>

          <div className="card kpi-tile-dia" style={ESTILO_TILE} title="Ventas del día ÷ pedidos válidos">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Ticket promedio</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span className="kpi-valor-dia" style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                {formatearMoneda(ticketAnimado)}
              </span>
              <BadgeTendencia pct={tTicket} etiqueta={compEtiqueta} textoIgual={compTextoIgual} titulo={compTitulo} />
            </div>
            <Sparkline valores={spTicket} color="var(--color-texto-secundario)" />
          </div>
        </div>

        {/* Fila de visualizaciones — dona de estados + últimos 7 días + Capital vs Forza */}
        <div className="viz-grid-dia stagger-list" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
          gap: isMobile ? '10px' : '14px'
        }}>
          {/* Dona: pedidos por estado (hoy). Estados en 0 → línea gris abajo */}
          <div className="card viz-card-dia" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ display: 'inline-flex' }}><CubeIcon className="w-4 h-4" /></span> Pedidos por estado (hoy)
            </div>
            <DonaEstados conteos={estadisticas.estadisticasEstados} total={estadisticas.totalPedidos} />
          </div>

          {/* Barras: pedidos válidos de los últimos 7 días (hoy resaltado) */}
          <div className="card viz-card-dia" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ display: 'inline-flex' }}><ChartBarIcon className="w-4 h-4" /></span> Pedidos últimos 7 días hábiles
            </div>
            <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
              {serie.map((p) => {
                const valor = p.data?.pedidosValidos || 0;
                const pctAltura = Math.max((valor / maxSerie) * 100, valor > 0 ? 4 : 2);
                return (
                  <div key={p.fecha} style={{ flex: 1, minWidth: 0, textAlign: 'center', display: 'flex', flexDirection: 'column' }} title={`${valor} pedidos válidos`}>
                    <div style={{ flex: 1, minHeight: '78px', display: 'flex', alignItems: 'flex-end' }}>
                      <div style={{
                        width: '100%',
                        height: `${pctAltura}%`,
                        backgroundColor: p.esHoy ? 'var(--color-primario)' : 'var(--color-borde)',
                        borderRadius: '4px 4px 0 0',
                        transform: barrasListas ? 'scaleY(1)' : 'scaleY(0)',
                        transformOrigin: 'bottom',
                        transition: 'transform 0.6s ease'
                      }} />
                    </div>
                    <div style={{ fontSize: '10px', marginTop: '4px', color: p.esHoy ? 'var(--color-primario-texto)' : 'var(--color-texto-secundario)', fontWeight: p.esHoy ? 'bold' : 'normal' }}>
                      {p.etiqueta}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Capital vs Forza: ventas y envíos del día */}
          <div className="card viz-card-dia" style={ESTILO_TILE}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ display: 'inline-flex' }}><BuildingOfficeIcon className="w-4 h-4" /></span> Capital vs Forza (ventas)
            </div>
            {totalCapitalForza > 0 && (
              <div style={{
                display: 'flex',
                height: '10px',
                borderRadius: 'var(--radio-sm)',
                overflow: 'hidden',
                backgroundColor: 'var(--color-borde)',
                marginBottom: '10px'
              }}>
                <div style={{
                  width: barrasListas ? `${pctCapital}%` : '0%',
                  backgroundColor: 'var(--color-info)',
                  transition: 'width 0.8s ease'
                }} />
                <div style={{
                  width: barrasListas ? `${100 - pctCapital}%` : '0%',
                  backgroundColor: 'var(--color-naranja)',
                  transition: 'width 0.8s ease'
                }} />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '12px' }}>
              <span style={{ color: 'var(--color-info-texto)', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <BuildingOfficeIcon className="w-4 h-4" /> CAPITAL · {estadisticas.desgloseCapitalForza?.Capital?.cantidad || 0}
              </span>
              <strong style={{ color: 'var(--color-info-texto)', fontVariantNumeric: 'tabular-nums' }}>{formatearMoneda(montoCapital)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '12px' }}>
              <span style={{ color: 'var(--color-naranja-texto)', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <BoltIcon className="w-4 h-4" /> FORZA · {estadisticas.desgloseCapitalForza?.FORZA?.cantidad || 0}
              </span>
              <strong style={{ color: 'var(--color-naranja-texto)', fontVariantNumeric: 'tabular-nums' }}>{formatearMoneda(montoForza)}</strong>
            </div>
            <div style={{ borderTop: '1px solid var(--color-borde-suave)', marginTop: '6px', paddingTop: '8px', fontSize: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                <span style={{ color: 'var(--color-texto-secundario)' }}>Envíos Capital</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatearMoneda(estadisticas.enviosCapitalForza?.Capital || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                <span style={{ color: 'var(--color-texto-secundario)' }}>Envíos Forza</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatearMoneda(estadisticas.enviosCapitalForza?.FORZA || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0 0', fontWeight: 'bold' }}>
                <span>Total envíos:</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatearMoneda((estadisticas.enviosCapitalForza?.Capital || 0) + (estadisticas.enviosCapitalForza?.FORZA || 0))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EstadisticasDiaWidget;